import React, { useState, useMemo } from 'react';
import { ChevronRight, ChevronDown } from 'lucide-react';
import { Checkbox } from '../../../components/ui/checkbox';
import { Label } from '../../../components/ui/label';

interface PermissionTreeProps {
  permissions: string[];
  onChange: (selectedPermissions: string[]) => void;
}

interface TreeNode {
  [key: string]: TreeNode | boolean;
}

const PermissionTree: React.FC<PermissionTreeProps> = ({
  permissions,
  onChange,
}) => {
  const [checkedNodes, setCheckedNodes] = useState<Set<string>>(new Set());

  // Build the tree structure
  const tree = useMemo(() => {
    const result: TreeNode = {};
    permissions.forEach((perm) => {
      const parts = perm.split(':');
      let current = result;
      parts.forEach((part, i) => {
        if (!current[part]) {
          current[part] = i === parts.length - 1 ? true : {};
        }
        if (typeof current[part] === 'object') {
          current = current[part] as TreeNode;
        }
      });
    });
    return result;
  }, [permissions]);

  // Get all leaf node permissions for a path
  const getLeafNodes = (node: TreeNode | boolean, path: string): string[] => {
    if (typeof node === 'boolean') return [path];
    return Object.entries(node).flatMap(([key, child]) =>
      getLeafNodes(child, path ? `${path}:${key}` : key),
    );
  };

  // Check if all children of a path are checked
  const areAllChildrenChecked = (
    path: string,
    node: TreeNode | boolean,
  ): boolean => {
    const leafNodes = getLeafNodes(node, path);
    return leafNodes.every((leaf) => checkedNodes.has(leaf));
  };

  // Check if some children of a path are checked
  const areSomeChildrenChecked = (
    path: string,
    node: TreeNode | boolean,
  ): boolean => {
    const leafNodes = getLeafNodes(node, path);
    return leafNodes.some((leaf) => checkedNodes.has(leaf));
  };

  const optimizePermissionsList = (leafPermissions: string[]): string[] => {
    const result = new Set<string>();

    // Get all possible prefixes at each level
    const allPrefixes = new Set<string>();
    leafPermissions.forEach((perm) => {
      const parts = perm.split(':');
      for (let i = 1; i < parts.length; i += 1) {
        allPrefixes.add(parts.slice(0, i).join(':'));
      }
    });

    // Check each prefix level for complete sets
    const usedPermissions = new Set<string>();
    Array.from(allPrefixes)
      .sort((a, b) => a.split(':').length - b.split(':').length) // Check shorter prefixes first
      .forEach((prefix) => {
        // Get all possible and selected permissions for this prefix
        const possiblePerms = permissions.filter((p) =>
          p.startsWith(`${prefix}:`),
        );
        const selectedPerms = leafPermissions.filter((p) =>
          p.startsWith(`${prefix}:`),
        );

        // If all possible permissions are selected and none are already covered by a wildcard
        if (
          possiblePerms.length === selectedPerms.length &&
          !selectedPerms.some((p) => usedPermissions.has(p))
        ) {
          result.add(`${prefix}:*`);
          selectedPerms.forEach((p) => usedPermissions.add(p));
        }
      });

    // Add remaining individual permissions that weren't covered by wildcards
    leafPermissions.forEach((perm) => {
      if (!usedPermissions.has(perm)) {
        result.add(perm);
      }
    });

    return Array.from(result);
  };

  const handleCheck =
    (path: string, node: TreeNode | boolean) =>
    (event: React.ChangeEvent<HTMLInputElement>) => {
      const isChecked = event.target.checked;
      const newChecked = new Set(checkedNodes);
      const leafNodes = getLeafNodes(node, path);

      if (isChecked) {
        leafNodes.forEach((leaf) => newChecked.add(leaf));
      } else {
        leafNodes.forEach((leaf) => newChecked.delete(leaf));
      }

      setCheckedNodes(newChecked);

      // Optimize the permissions list before returning
      const checkedLeafNodes = Array.from(newChecked);
      const optimizedPermissions = optimizePermissionsList(checkedLeafNodes);
      onChange(optimizedPermissions);
    };

  const [expandedNodes, setExpandedNodes] = useState<Set<string>>(new Set());

  const toggleExpanded = (path: string) => {
    const newExpanded = new Set(expandedNodes);
    if (newExpanded.has(path)) {
      newExpanded.delete(path);
    } else {
      newExpanded.add(path);
    }
    setExpandedNodes(newExpanded);
  };

  const renderNode = (
    node: TreeNode | boolean,
    path: string,
    level: number = 0,
  ): React.ReactNode => {
    const isLeaf = typeof node === 'boolean';
    const isChecked = isLeaf
      ? checkedNodes.has(path)
      : areAllChildrenChecked(path, node);
    const isIndeterminate =
      !isLeaf && !isChecked && areSomeChildrenChecked(path, node);
    const isExpanded = expandedNodes.has(path);
    const label = path.split(':').pop();

    return (
      <div key={path} className="w-full">
        <div
          className="flex items-center gap-2 py-1 hover:bg-muted/50 rounded"
          style={{ paddingLeft: `${level * 20}px` }}
        >
          {!isLeaf && (
            <button
              type="button"
              onClick={() => toggleExpanded(path)}
              className="p-1 hover:bg-muted rounded flex-shrink-0"
            >
              {isExpanded ? (
                <ChevronDown className="h-4 w-4" />
              ) : (
                <ChevronRight className="h-4 w-4" />
              )}
            </button>
          )}
          {isLeaf && <div className="w-6" />}

          <div className="flex items-center space-x-2">
            <Checkbox
              id={`checkbox-${path}`}
              checked={isChecked}
              onCheckedChange={(checked: boolean) => {
                const event = {
                  target: { checked: !!checked },
                } as React.ChangeEvent<HTMLInputElement>;
                handleCheck(path, node)(event);
              }}
              className={
                isIndeterminate ? 'data-[state=checked]:bg-primary/50' : ''
              }
            />
            <Label
              htmlFor={`checkbox-${path}`}
              className="text-sm font-mono cursor-pointer"
            >
              {label}
            </Label>
          </div>
        </div>

        {!isLeaf && isExpanded && (
          <div>
            {Object.entries(node as TreeNode).map(([key, child]) =>
              renderNode(child, path ? `${path}:${key}` : key, level + 1),
            )}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="border rounded-lg p-4 max-h-96 overflow-y-auto">
      {Object.entries(tree).map(([key, node]) => renderNode(node, key, 0))}
    </div>
  );
};

export default PermissionTree;
